import { Fragment, useState } from 'react'
import { BellRing } from 'lucide-react'
import { ApiError, Button, EMPLOYMENT_TYPE_BADGE, TIER_LABELS } from '../../core/index.js'
import type { PeriodWorkerReadiness, RemindAllResult } from '../types.js'
import { RemindAllButton } from './RemindAllButton.js'
import { filterCounts, matchesFilter, READINESS_FILTERS, REMINDER_ASKS, remindedRecently, type ReadinessFilter } from './monthReadinessData.js'
import { describeDay } from './preferencesText.js'
import { ShiftPreferencesPanel } from './ShiftPreferencesPanel.js'

function shortDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone })
}

function PreferencesCell({ worker, timezone }: { worker: PeriodWorkerReadiness; timezone: string }) {
  if (!worker.bids) {
    return <span className="text-ink-mid">Not needed</span>
  }
  if (!worker.preferences) {
    return <span className="font-bold text-coral">None</span>
  }
  return <span className="text-ink">Saved {shortDate(worker.preferences.updatedAt, timezone)}</span>
}

function DraftCell({ worker, timezone }: { worker: PeriodWorkerReadiness; timezone: string }) {
  const draft = worker.draft
  if (!draft) {
    return <span className="text-ink-mid">-</span>
  }
  if (draft.isStale) {
    return <span className="font-bold text-coral">{draft.status === 'accepted' ? 'Submitted, out of date' : 'Built, out of date'}</span>
  }
  if (draft.status === 'accepted') {
    return <span className="font-bold text-green">Submitted {draft.acceptedAt ? shortDate(draft.acceptedAt, timezone) : ''}</span>
  }
  return <span className="text-ink">Built, not submitted</span>
}

function whenSent(iso: string, timezone: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timezone })
}

/**
 * The Remind cell: a button for a worker who bids and has not saved preferences (their row's `reminder`: asking while they are open, telling what the build does without them once closed), which sends one message;
 * "Reminded ..." once sent, until a day has passed. Without `onRemind` (the admin's read-only view) only when it went.
 */
function RemindCell({ worker, timezone, onRemind }: { worker: PeriodWorkerReadiness; timezone: string; onRemind?: (workerId: string) => Promise<void> }) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sentNote = worker.remindedAt ? <span className="text-[10px] text-ink-mid">Reminded {whenSent(worker.remindedAt, timezone)}</span> : null
  if (!onRemind || !worker.reminder || remindedRecently(worker.remindedAt, new Date())) {
    return sentNote ?? <span className="text-ink-mid">-</span>
  }
  async function send(): Promise<void> {
    setPending(true)
    setError(null)
    try {
      await onRemind?.(worker.workerId)
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'The reminder could not be sent. Try again.')
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="flex flex-col items-start gap-0.5" onClick={(event) => event.stopPropagation()}>
      <Button icon={BellRing} size="sm" variant="secondary" disabled={pending} title={REMINDER_ASKS[worker.reminder]} onClick={() => void send()}>
        {pending ? 'Sending…' : 'Remind'}
      </Button>
      {sentNote}
      {error && (
        <span role="alert" className="text-[10px] text-coral">
          {error}
        </span>
      )}
    </div>
  )
}

function askedOf(worker: PeriodWorkerReadiness): string {
  const parts: string[] = []
  if (worker.preferences) {
    const { hours, ptoDays, daysOff } = worker.preferences
    parts.push(hours.min === hours.max ? `${hours.max} h` : `${hours.min}-${hours.max} h`)
    if (ptoDays > 0) {
      parts.push(`${ptoDays} PTO`)
    }
    if (daysOff > 0) {
      parts.push(`${daysOff} off`)
    }
  }
  if (worker.draft) {
    parts.push(`draft ${worker.draft.shifts} shifts · ${worker.draft.hours} h`)
  }
  return parts.join(' · ') || '-'
}

/**
 * Every worker on the roster for the month in the order the run drafts them, with whether they bid, what they saved
 * and where their draft stands (month readiness plan). Filter pills say how many each holds; a row opens the worker's
 * preferences for the month, read-only.
 */
export function ReadinessTable({ workers, period, timezone, filter, onFilter, onRemind, onRemindAll }: { workers: readonly PeriodWorkerReadiness[]; period: string; /** The facility's zone: dates read as the facility's days. */ timezone: string; filter: ReadinessFilter; onFilter: (filter: ReadinessFilter) => void; /** Staff's Remind; absent, the table only says when reminders went. */ onRemind?: (workerId: string) => Promise<void>; /** Staff's Remind all; absent, no button. */ onRemindAll?: () => Promise<RemindAllResult> }) {
  const [openId, setOpenId] = useState<string | null>(null)
  const counts = filterCounts(workers)
  const shown = workers.filter((worker) => matchesFilter(worker, filter))
  return (
    <div className="flex flex-col gap-2">
      {/* With rows to show, Remind all heads the Remind column, in line with the rows' own buttons; with none, it sits here. */}
      {onRemindAll && shown.length === 0 && <RemindAllButton workers={workers} onRemindAll={onRemindAll} />}
      <div role="radiogroup" aria-label="Show" className="flex flex-wrap gap-1">
        {READINESS_FILTERS.map(({ key, label }) => (
          <button key={key} type="button" role="radio" aria-checked={filter === key} onClick={() => onFilter(key)} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${filter === key ? 'border-marine bg-marine text-white' : 'border-marine/30 text-ink-mid hover:border-marine'}`}>
            {label} · {counts[key]}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="text-[11px] text-ink-mid">{workers.length === 0 ? 'Nobody is on the roster for this month.' : 'Nobody matches this filter.'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-[11px]">
            <thead>
              <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid [&>th]:align-bottom">
                <th className="py-1 pr-2 font-bold">Worker</th>
                <th className="py-1 pr-2 font-bold">Rank</th>
                <th className="py-1 pr-2 font-bold">Bids?</th>
                <th className="py-1 pr-2 font-bold">Preferences</th>
                <th className="py-1 pr-2 font-bold">Draft</th>
                <th className="py-1 pr-2 font-bold">Asked</th>
                <th className="py-1 font-bold">
                  <div className="flex flex-col items-start gap-1">
                    {onRemindAll && <RemindAllButton workers={workers} onRemindAll={onRemindAll} stacked />}
                    <span>Remind</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((worker) => {
                const isOpen = openId === worker.workerId
                const badge = EMPLOYMENT_TYPE_BADGE[worker.employmentType]
                return (
                  <Fragment key={worker.workerId}>
                    <tr className="cursor-pointer border-t border-marine/10 align-top hover:bg-canvas" onClick={() => setOpenId(isOpen ? null : worker.workerId)}>
                      <td className="py-1.5 pr-2">
                        <button type="button" aria-expanded={isOpen} className="text-left font-bold text-ink underline decoration-marine/30 underline-offset-2 hover:text-marine">
                          {worker.name}
                        </button>
                        {badge && <span className="ml-1 rounded bg-gold/30 px-1 text-[9px] font-bold text-ink">{badge}</span>}
                        {worker.leaveDate && worker.leaveDate.slice(0, 7) === period && <div className="text-[10px] text-ink-mid">Leaves {describeDay(worker.leaveDate)}</div>}
                      </td>
                      <td className="py-1.5 pr-2 text-ink-mid">
                        {TIER_LABELS[worker.tier]} · {Math.round(worker.score)}
                      </td>
                      <td className="py-1.5 pr-2 text-ink-mid">{worker.bids ? 'Bids' : 'Assigned'}</td>
                      <td className="py-1.5 pr-2">
                        <PreferencesCell worker={worker} timezone={timezone} />
                      </td>
                      <td className="py-1.5 pr-2">
                        <DraftCell worker={worker} timezone={timezone} />
                      </td>
                      <td className="py-1.5 pr-2 text-ink-mid">{askedOf(worker)}</td>
                      <td className="py-1.5">
                        <RemindCell worker={worker} timezone={timezone} onRemind={onRemind} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} className="border-t border-marine/10 bg-canvas/60">
                          <ShiftPreferencesPanel workerId={worker.workerId} period={period} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-ink-mid/80">In the order the run drafts them: tier, then score, then seniority. An assigned tier saves no preferences; its draft is built from the facility&apos;s rules.</p>
    </div>
  )
}
